import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getAssetsRegisterCounts, type AssetsRegisterCounts } from '@/services/api/assetsApi';
import type { OrgScope } from '@/services/api/organisationScope';

interface AssetsRegisterCountsState {
	counts: AssetsRegisterCounts;
	loading: boolean;
	error: string | null;
}

const initialState: AssetsRegisterCountsState = {
	counts: {
		assets: 0,
		categories: 0,
		groups: 0,
		types: 0,
	},
	loading: false,
	error: null,
};

/**
 * The scope is a required thunk argument rather than something the thunk resolves for itself.
 *
 * A thunk cannot call `useListOrgScope`, so resolving it here would mean re-deriving the role rules
 * outside the one hook that owns them — the exact duplication that let '' mean two different things
 * before this contract. Every dispatch site passes `useAssetsRegisterCountsScope()` so the breadth
 * is identical wherever the badges are refreshed from.
 */
export const fetchAssetsRegisterCounts = createAsyncThunk('assetsRegisterCounts/fetch', async (scope: OrgScope) => {
	const data = await getAssetsRegisterCounts(scope);
	return data;
});

const assetsRegisterCountsSlice = createSlice({
	name: 'assetsRegisterCounts',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchAssetsRegisterCounts.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchAssetsRegisterCounts.fulfilled, (state, action) => {
				state.loading = false;
				state.counts = action.payload;
			})
			.addCase(fetchAssetsRegisterCounts.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message || 'Failed to fetch counts';
			});
	},
});

export default assetsRegisterCountsSlice.reducer;
